import type {
  ChatResponse,
  CognitiveGraph,
  LearningJobStatus,
  MemoryRecord,
  SuggestedMemoryRecord
} from "./api/types";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  /** Presente apenas em mensagens do assistente ja concluidas. */
  response?: ChatResponse;
  pending?: boolean;
  error?: string;
  /**
   * Desfecho do aprendizado desta mensagem. Sem isso a etiqueta "aprendendo…" nunca resolvia:
   * o job era iniciado, a UI anunciava, e ninguem jamais perguntava como terminou.
   */
  learning?: { status: LearningJobStatus; suggestionCount: number; error: string | null };
}

export interface AppState {
  userId: string;
  conversationId: string | null;
  responseMode: string;
  messages: ChatMessage[];
  /** Resposta do ultimo turno concluido: alimenta o pipeline e o destaque no grafo. */
  lastResponse: ChatResponse | null;
  graph: CognitiveGraph | null;
  graphLoading: boolean;
  graphError: string | null;
  focusKey: string | null;
  depth: number;
  selectedNodeKey: string | null;
  /**
   * Memorias que sustentam um no. Carregadas uma vez pelo painel de detalhe e publicadas aqui
   * para que o grafo desenhe os satelites sem uma segunda ida a API.
   */
  evidence: MemoryRecord[];
  /**
   * No a que a evidencia acima pertence. Guardar a origem junto do dado dispensa limpar a lista
   * ao trocar de selecao: evidencia de outro no simplesmente nao casa e nao e desenhada. Limpar
   * de dentro do render reentrava no proprio assinante da store e cancelava a carga em voo.
   */
  evidenceFor: string | null;
  /** Sugestoes aguardando curadoria. Aprovar uma delas e o unico caminho para um no nascer. */
  suggestions: SuggestedMemoryRecord[];
  /** Ids em transito para aprovacao ou rejeicao, para desabilitar o par de botoes certo. */
  suggestionsBusy: string[];
  apiOnline: boolean;
}

type Listener = (state: AppState) => void;

const DEFAULT_USER_ID = "00000000-0000-0000-0000-000000000001";
const USER_STORAGE_KEY = "arti-user-id";
const CONVERSATION_STORAGE_KEY = "arti-conversation-id";

function readStored(key: string, fallback: string | null): string | null {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

function writeStored(key: string, value: string | null): void {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch {
    // Armazenamento indisponivel (aba privada, cota). A sessao segue apenas em memoria.
  }
}

class Store {
  private listeners: Listener[] = [];

  state: AppState = {
    userId: readStored(USER_STORAGE_KEY, DEFAULT_USER_ID) ?? DEFAULT_USER_ID,
    conversationId: readStored(CONVERSATION_STORAGE_KEY, null),
    responseMode: "KnowledgePreferred",
    messages: [],
    lastResponse: null,
    graph: null,
    graphLoading: false,
    graphError: null,
    focusKey: null,
    depth: 1,
    selectedNodeKey: null,
    evidence: [],
    evidenceFor: null,
    suggestions: [],
    suggestionsBusy: [],
    apiOnline: true
  };

  subscribe(listener: Listener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((candidate) => candidate !== listener);
    };
  }

  update(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
    if (patch.userId !== undefined) {
      writeStored(USER_STORAGE_KEY, patch.userId);
    }
    if (patch.conversationId !== undefined) {
      writeStored(CONVERSATION_STORAGE_KEY, patch.conversationId);
    }
    for (const listener of this.listeners) {
      listener(this.state);
    }
  }

  upsertMessage(message: ChatMessage): void {
    const existing = this.state.messages.findIndex((candidate) => candidate.id === message.id);
    const messages =
      existing >= 0
        ? this.state.messages.map((candidate, index) => (index === existing ? message : candidate))
        : [...this.state.messages, message];
    this.update({ messages });
  }
}

export const store = new Store();
